import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { Table } from "reactstrap";

import { lang } from "../../../middleware/lang";
import { formatNumber } from "../../../middleware/common-functions";
import {
  GET_CONNECT_PAYOUTS,
  GetConnectPayoutsResponse,
} from "./queries";

const statusStyles: Record<string, { text_es: string; text_en: string; color: string; bg: string }> = {
  paid: { text_es: "Pagado", text_en: "Paid", color: "#48BB78", bg: "#e6f7ee" },
  pending: { text_es: "Pendiente", text_en: "Pending", color: "#FFBB28", bg: "#fff8e1" },
  in_transit: { text_es: "En transito", text_en: "In transit", color: "#4299E1", bg: "#ebf5ff" },
  canceled: { text_es: "Cancelado", text_en: "Canceled", color: "#FF6028", bg: "#fff0eb" },
  failed: { text_es: "Fallido", text_en: "Failed", color: "#FF6028", bg: "#fff0eb" },
};

const Payouts: React.FC = () => {
  const [cursor, setCursor] = useState<string | undefined>();
  const [allData, setAllData] = useState<any[]>([]);

  const { data, loading } = useQuery<GetConnectPayoutsResponse>(
    GET_CONNECT_PAYOUTS,
    {
      variables: { limit: 20, starting_after: cursor },
      fetchPolicy: "cache-and-network",
      onCompleted: (result) => {
        if (cursor && result?.getConnectPayouts.data) {
          setAllData((prev) => [...prev, ...result.getConnectPayouts.data]);
        }
      },
    }
  );

  const payouts = cursor ? allData : data?.getConnectPayouts.data || [];
  const hasMore = data?.getConnectPayouts.has_more || false;

  const loadMore = () => {
    const last = (data?.getConnectPayouts.data || []).slice(-1)[0];
    if (last) {
      setAllData(payouts);
      setCursor(last.id);
    }
  };

  if (!loading && payouts.length === 0) {
    return (
      <div className="text-center py-5">
        <i className="fas fa-piggy-bank fa-2x text-muted mb-3 d-block" />
        <p className="text-muted">
          {lang("No hay depositos aun", "No payouts yet")}
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #ebebeb",
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      <Table responsive hover className="mb-0">
        <thead>
          <tr style={{ background: "#f8f9fa" }}>
            <th>{lang("Fecha creacion", "Created")}</th>
            <th>{lang("Fecha de llegada", "Arrival date")}</th>
            <th>{lang("Estado", "Status")}</th>
            <th>{lang("Metodo", "Method")}</th>
            <th className="text-right">{lang("Monto", "Amount")}</th>
          </tr>
        </thead>
        <tbody>
          {payouts.map((p) => {
            const status = statusStyles[p.status] || statusStyles.pending;
            return (
              <tr key={p.id}>
                <td style={{ whiteSpace: "nowrap", fontSize: 13 }}>
                  {new Date(p.created).toLocaleDateString("es-MX", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </td>
                <td style={{ whiteSpace: "nowrap", fontSize: 13 }}>
                  {new Date(p.arrival_date).toLocaleDateString("es-MX", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </td>
                <td>
                  <span
                    style={{
                      fontSize: 12,
                      padding: "2px 8px",
                      borderRadius: 4,
                      background: status.bg,
                      color: status.color,
                      fontWeight: 600,
                    }}
                  >
                    {lang(status.text_es, status.text_en)}
                  </span>
                </td>
                <td style={{ fontSize: 13, textTransform: "capitalize" }}>
                  {p.method || "-"}
                </td>
                <td className="text-right" style={{ fontWeight: 700 }}>
                  ${formatNumber(p.amount)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {loading && (
        <div className="text-center py-3">
          <span className="spinner-border spinner-border-sm" />
        </div>
      )}

      {hasMore && !loading && (
        <div className="text-center py-3">
          <button
            className="btn btn-sm"
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: 6,
              fontWeight: 600,
            }}
            onClick={loadMore}
          >
            {lang("Cargar mas", "Load more")}
          </button>
        </div>
      )}
    </div>
  );
};

export default Payouts;
